import React, { useState, useEffect } from 'react';
import './MicroSurvey.css';

interface RankedReason {
  rank: number;
  value: string;
  label: string;
  category: 'gist' | 'verbatim';
}

interface MicroSurveyProps {
  emailId?: string;
  config?: any;
  taskType?: 'email' | 'device' | 'coding';
  scenarioType?: string; // For device tasks: visual type (e.g., 'antivirus-alert', 'email-attachment-preview')
  emailCategory?: 'system-notice' | 'colleague-work' | 'authority-request' | 'financial-transaction' | 'policy-compliance' | 'security-alert' | 'payroll-document' | 'news-article'; // For task-specific FTT options
  actionTaken?: 'clicked' | 'notClicked' | 'proceed' | 'decline' | 'engage' | 'verify' | 'ignore' | 'report' | 'share' | 'skip' | 'flag';
  onSubmit?: (response: {
    reason: string;
    customReason?: string;
    reasonCategory?: 'gist' | 'verbatim' | 'other' | 'task_need' | 'external_pressure' | 'motivation' | 'risk_assessment';
    confidence?: number;
    metacognitiveCertainty?: number;
    perceivedTimePressure?: number;
    // New ranking fields
    rankedReasons?: RankedReason[];
    gistDominanceScore?: number;
    // Position bias analysis fields (updated for single list)
    primaryReasonKeySelected?: string;
    routeSelected?: 'gist' | 'verbatim';
    presentedOrder?: string[];
    selectedIndices?: number[];
    // Legacy fields kept for backward compat
    gistOrder?: string[];
    verbatimOrder?: string[];
    selectedIndexWithinColumn?: number;
    decisionLatencyMs?: number;
    // Hidden timing data for microsurvey completion analysis
    totalMicroSurveyTimeMs?: number;
    step1TimeMs?: number | null;
    step2TimeMs?: number | null;
  }) => void;
  onResponse?: (response: string) => void;
  onClose?: () => void;
  onSkip?: () => void;
}

const MicroSurvey: React.FC<MicroSurveyProps> = ({
  emailId,
  config,
  taskType = 'email',
  scenarioType,
  emailCategory,
  actionTaken,
  onSubmit,
  onResponse,
  onClose,
  onSkip
}) => {
  console.log('[MicroSurvey] Component mounted with props:', {
    emailId,
    taskType,
    actionTaken,
    hasOnSubmit: !!onSubmit,
    hasOnResponse: !!onResponse,
    hasOnClose: !!onClose,
    hasOnSkip: !!onSkip
  });
  const effectiveOnClose = onClose || onSkip || (() => {});
  // rankMap: value -> rank (1, 2, or 3). Only assigned items are in the map.
  const [rankMap, setRankMap] = useState<Record<string, number>>({});
  const [customReason, setCustomReason] = useState('');
  const [confidence, setConfidence] = useState(50);
  const [metacognitiveCertainty, setMetacognitiveCertainty] = useState(50);
  const [perceivedTimePressure, setPerceivedTimePressure] = useState(50);
  const [selectionStartTime] = useState(Date.now());
  const [currentStep, setCurrentStep] = useState(1);
  const [microSurveyStartTime] = useState(Date.now());
  const [step1CompletedTime, setStep1CompletedTime] = useState<number | null>(null);
  const [shuffledList, setShuffledList] = useState<Array<{value: string, label: string, category: 'gist' | 'verbatim'}> | null>(null);

  // Fisher-Yates shuffle implementation (unbiased)
  const fisherYatesShuffle = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Get FTT-aligned email survey options based on email category AND action taken
  const getEmailPrimaryReasons = () => {
    if (actionTaken === 'verify') {
      return {
        gist: [
          { value: 'wanted_reassurance', label: 'I wanted reassurance before acting' },
          { value: 'important_but_unusual', label: 'The request seemed important but unusual' },
          { value: 'something_felt_off', label: 'Something felt off' },
          { value: 'normal_verification_habit', label: 'Verifying first is my normal habit' },
          { value: 'avoid_unnecessary_risk', label: 'I wanted to avoid unnecessary risk' }
        ],
        verbatim: [
          { value: 'verify_sender_separately', label: 'I would contact the sender through a known channel' },
          { value: 'open_official_portal', label: 'I would open the official portal directly' },
          { value: 'inspect_sender_domain', label: 'I wanted to confirm the sender address or domain' },
          { value: 'inspect_link_destination', label: 'I wanted to confirm the actual link destination' },
          { value: 'resolve_conflicting_details', label: 'Specific details did not fully match the request' }
        ]
      };
    }

    const defaultOptions = (actionTaken === 'engage' || actionTaken === 'clicked') ? {
      gist: [
        { value: 'email_seemed_trustworthy', label: 'The email seemed trustworthy overall' },
        { value: 'request_fit_work_context', label: 'The request fit my work context' },
        { value: 'message_felt_familiar', label: 'The message felt familiar or routine' },
        { value: 'action_seemed_useful', label: 'Taking action seemed useful or necessary' },
        { value: 'concern_seemed_low', label: 'The concern seemed low' }
      ],
      verbatim: [
        { value: 'sender_domain_matched', label: 'The sender or domain matched what I expected' },
        { value: 'destination_or_file_matched', label: 'The link destination or attachment matched the request' },
        { value: 'details_fit_context', label: 'The dates and request details fit the context' },
        { value: 'recognized_person_service', label: 'I recognized the person, service, or process' },
        { value: 'reference_supported_request', label: 'A contact, policy, or reference supported the request' }
      ]
    } : actionTaken === 'report' ? {
      gist: [
        { value: 'email_felt_suspicious', label: 'The email felt suspicious overall' },
        { value: 'familiar_phishing_pattern', label: 'It matched a familiar phishing pattern' },
        { value: 'pressure_felt_manipulative', label: 'The urgency or pressure felt manipulative' },
        { value: 'request_felt_inappropriate', label: 'The request felt inappropriate' },
        { value: 'concern_outweighed_need', label: 'The concern outweighed any need to act' }
      ],
      verbatim: [
        { value: 'sender_or_domain_mismatch', label: 'The sender address or domain did not match' },
        { value: 'link_or_file_mismatch', label: 'The link destination or attachment looked wrong' },
        { value: 'timing_or_context_mismatch', label: 'The timing or stated context did not fit' },
        { value: 'sensitive_action_requested', label: 'It requested credentials, payment, software, or other sensitive action' },
        { value: 'source_not_confirmable', label: 'The source could not be confirmed from the information shown' }
      ]
    } : {
      gist: [
        { value: 'not_relevant_now', label: 'The email did not seem relevant right now' },
        { value: 'low_priority', label: 'It seemed low priority' },
        { value: 'uncertain_preferred_no_action', label: 'I was unsure and preferred not to act' },
        { value: 'not_worth_attention', label: 'It did not seem worth further attention' },
        { value: 'preferred_to_defer', label: 'I preferred to defer it' }
      ],
      verbatim: [
        { value: 'no_action_required', label: 'I did not see a specific action I needed to take' },
        { value: 'outside_role_or_scope', label: 'The request was outside my role or scope' },
        { value: 'deadline_did_not_require_response', label: 'The timing or deadline did not require a response from me' },
        { value: 'could_use_official_channel_later', label: 'I could use an official channel later if needed' },
        { value: 'insufficient_evidence_for_action', label: 'There was not enough information to engage or report it' }
      ]
    };

    // Broad action-specific options are used in the live study so every answer
    // can plausibly apply to every stimulus. Several legacy category lists
    // below asserted facts participants could not know (for example, that they
    // had already contacted IT). Retain those lists only for a future,
    // scenario-level validated configuration.
    if (!config?.useCategorySpecificEmailReasons) {
      return defaultOptions;
    }

    switch (emailCategory) {
      case 'system-notice':
        if (actionTaken === 'engage' || actionTaken === 'clicked') {
          return {
            gist: [
              { value: 'routine_update', label: 'Routine system update' },
              { value: 'it_communication', label: 'IT department communication' },
              { value: 'expected_notice', label: 'Expected this notice' },
              { value: 'seemed_official', label: 'Seemed official' },
              { value: 'need_to_comply', label: 'Need to stay compliant' }
            ],
            verbatim: [
              { value: 'checked_sender_domain', label: 'Checked sender email address/domain' },
              { value: 'verified_link_destination', label: 'Verified link goes to company site' },
              { value: 'read_specific_details', label: 'Read specific dates/times mentioned' },
              { value: 'confirmed_sender_name', label: 'Confirmed sender name is known' },
              { value: 'examined_formatting', label: 'Examined professional formatting' }
            ]
          };
        } else if (actionTaken === 'report') {
          return {
            gist: [
              { value: 'impersonation_attempt', label: 'IT impersonation attempt' },
              { value: 'felt_suspicious', label: 'Felt suspicious overall' },
              { value: 'too_urgent', label: 'Too urgent/threatening' },
              { value: 'protect_others', label: 'Want to protect colleagues' },
              { value: 'phishing_pattern', label: 'Classic phishing pattern' }
            ],
            verbatim: [
              { value: 'wrong_domain', label: 'Wrong/suspicious sender domain' },
              { value: 'suspicious_link_dest', label: 'Suspicious link destination' },
              { value: 'unscheduled_maintenance', label: 'No such scheduled event' },
              { value: 'grammar_errors', label: 'Grammar/spelling errors found' },
              { value: 'verified_with_it', label: 'Verified with IT - not legitimate' }
            ]
          };
        } else { // ignore
          return {
            gist: [
              { value: 'not_my_concern', label: 'Not my immediate concern' },
              { value: 'can_wait', label: 'Can address later if needed' },
              { value: 'unsure_legitimacy', label: 'Unsure about legitimacy' },
              { value: 'too_busy', label: 'Too busy right now' },
              { value: 'low_priority', label: 'Low priority notification' }
            ],
            verbatim: [
              { value: 'no_deadline_urgency', label: 'No immediate deadline mentioned' },
              { value: 'unclear_sender', label: 'Sender identity unclear' },
              { value: 'check_later', label: 'Will verify with IT later' },
              { value: 'unfamiliar_format', label: 'Unfamiliar email format' },
              { value: 'optional_content', label: 'Content appears optional' }
            ]
          };
        }

      case 'colleague-work':
        if (actionTaken === 'engage' || actionTaken === 'clicked') {
          return {
            gist: [
              { value: 'team_member', label: 'From my team member' },
              { value: 'work_document', label: 'Work-related document sharing' },
              { value: 'meeting_followup', label: 'Meeting follow-up' },
              { value: 'action_items', label: 'Action items for my tasks' },
              { value: 'project_workflow', label: 'Part of project workflow' }
            ],
            verbatim: [
              { value: 'verified_sender_name', label: 'Verified sender name' },
              { value: 'checked_domain', label: 'Checked @company.com domain' },
              { value: 'confirmed_attachment', label: 'Confirmed attachment filename' },
              { value: 'read_details', label: 'Read action item details' },
              { value: 'verified_reference', label: 'Verified SharePoint/project reference' }
            ]
          };
        } else if (actionTaken === 'report') {
          return {
            gist: [
              { value: 'colleague_impersonation', label: 'Colleague impersonation' },
              { value: 'suspicious_request', label: 'Suspicious request from "colleague"' },
              { value: 'felt_wrong', label: 'Something felt wrong' },
              { value: 'protect_team', label: 'Protect team from threat' },
              { value: 'spoofing_detected', label: 'Email spoofing detected' }
            ],
            verbatim: [
              { value: 'sender_domain_mismatch', label: 'Sender domain doesn\'t match company' },
              { value: 'unusual_attachment', label: 'Unusual attachment type (.exe, .zip)' },
              { value: 'uncharacteristic_language', label: 'Language uncharacteristic of colleague' },
              { value: 'no_previous_context', label: 'No previous meeting/context exists' },
              { value: 'verified_colleague_didnt_send', label: 'Verified colleague didn\'t send this' }
            ]
          };
        } else { // ignore
          return {
            gist: [
              { value: 'not_addressed_to_me', label: 'Not directly relevant to me' },
              { value: 'can_defer', label: 'Can defer to later' },
              { value: 'optional_info', label: 'Seems like optional info' },
              { value: 'low_priority_task', label: 'Low priority task' },
              { value: 'already_handled', label: 'Already handled elsewhere' }
            ],
            verbatim: [
              { value: 'no_deadline_specified', label: 'No specific deadline mentioned' },
              { value: 'cc_not_to', label: 'CC\'d, not directly addressed' },
              { value: 'fyi_only', label: 'Marked as FYI only' },
              { value: 'already_completed', label: 'Task already completed' },
              { value: 'defer_to_teammate', label: 'Will defer to other team member' }
            ]
          };
        }

      case 'authority-request':
        if (actionTaken === 'engage' || actionTaken === 'clicked') {
          return {
            gist: [
              { value: 'ceo_authority', label: 'CEO/executive authority request' },
              { value: 'executive_matter', label: 'Confidential executive matter' },
              { value: 'business_deadline', label: 'Urgent business deadline' },
              { value: 'board_prep', label: 'Board/meeting preparation' },
              { value: 'dept_input', label: 'Required my department input' }
            ],
            verbatim: [
              { value: 'checked_sender_name', label: 'Checked executive/assistant name' },
              { value: 'verified_urgency', label: 'Verified urgency is plausible' },
              { value: 'read_deadline', label: 'Read the deadline mentioned' },
              { value: 'executive_signature', label: 'Executive signature/title present' },
              { value: 'business_context_fits', label: 'Business context fits current work' }
            ]
          };
        } else if (actionTaken === 'report') {
          return {
            gist: [
              { value: 'ceo_fraud', label: 'CEO fraud/BEC attack' },
              { value: 'authority_manipulation', label: 'Authority manipulation tactic' },
              { value: 'too_suspicious', label: 'Too suspicious overall' },
              { value: 'protect_company', label: 'Protect company from fraud' },
              { value: 'impersonation_clear', label: 'Clear impersonation attempt' }
            ],
            verbatim: [
              { value: 'wrong_domain', label: 'Wrong/suspicious sender domain' },
              { value: 'unusual_request_method', label: 'Executive wouldn\'t request this way' },
              { value: 'external_login_required', label: 'External login required (red flag)' },
              { value: 'verified_exec_didnt_send', label: 'Verified with executive - didn\'t send' },
              { value: 'grammar_inconsistent', label: 'Grammar/tone inconsistent with sender' }
            ]
          };
        } else { // ignore
          return {
            gist: [
              { value: 'not_my_responsibility', label: 'Not my responsibility' },
              { value: 'seems_odd', label: 'Seems odd but uncertain' },
              { value: 'defer_to_manager', label: 'Will defer to my manager' },
              { value: 'too_busy_verify', label: 'Too busy to verify now' },
              { value: 'uncertain_authenticity', label: 'Uncertain about authenticity' }
            ],
            verbatim: [
              { value: 'outside_chain_command', label: 'Outside my chain of command' },
              { value: 'unusual_direct_contact', label: 'Unusual for direct contact from executive' },
              { value: 'no_manager_cc', label: 'My manager not CC\'d (unusual)' },
              { value: 'verify_later', label: 'Will verify through proper channels later' },
              { value: 'unfamiliar_domain', label: 'Unfamiliar link/file-sharing domain' }
            ]
          };
        }

      case 'financial-transaction':
        if (actionTaken === 'engage' || actionTaken === 'clicked') {
          return {
            gist: [
              { value: 'payment_auth', label: 'Payment authorization needed' },
              { value: 'invoice_processing', label: 'Vendor invoice processing' },
              { value: 'financial_approval', label: 'Time-sensitive financial approval' },
              { value: 'finance_workflow', label: 'Standard finance workflow' },
              { value: 'eod_deadline', label: 'End-of-day deadline' }
            ],
            verbatim: [
              { value: 'verified_vendor_name', label: 'Verified vendor/service name mentioned' },
              { value: 'checked_amount', label: 'Checked the invoice amount' },
              { value: 'examined_sender_domain', label: 'Examined sender email domain' },
              { value: 'confirmed_due_date', label: 'Confirmed payment deadline/due date' },
              { value: 'verified_link_destination', label: 'Verified link goes to expected site' }
            ]
          };
        } else if (actionTaken === 'report') {
          return {
            gist: [
              { value: 'invoice_fraud', label: 'Invoice fraud attempt' },
              { value: 'payment_scam', label: 'Payment scam detected' },
              { value: 'bec_attack', label: 'Business email compromise' },
              { value: 'financial_threat', label: 'Financial threat to company' },
              { value: 'spoofed_vendor', label: 'Spoofed vendor email' }
            ],
            verbatim: [
              { value: 'wrong_domain', label: 'Wrong/suspicious sender domain' },
              { value: 'vendor_not_recognized', label: 'Vendor not in approved list' },
              { value: 'suspicious_portal_url', label: 'Suspicious payment portal URL' },
              { value: 'verified_finance_didnt_send', label: 'Verified finance didn\'t send this' },
              { value: 'unusual_urgency', label: 'Unusual urgency for payment' }
            ]
          };
        } else { // ignore
          return {
            gist: [
              { value: 'not_authorized', label: 'Not authorized to approve' },
              { value: 'outside_my_role', label: 'Outside my role' },
              { value: 'defer_to_finance', label: 'Defer to finance team' },
              { value: 'unclear_legitimacy', label: 'Unclear legitimacy' },
              { value: 'verify_first', label: 'Need to verify first' }
            ],
            verbatim: [
              { value: 'no_approval_authority', label: 'No approval authority for this amount' },
              { value: 'unfamiliar_vendor', label: 'Unfamiliar vendor name' },
              { value: 'check_with_finance', label: 'Will check with finance department' },
              { value: 'unusual_domain', label: 'Unusual sender domain' },
              { value: 'no_purchase_order', label: 'No purchase order reference provided' }
            ]
          };
        }

      case 'policy-compliance':
        if (actionTaken === 'engage' || actionTaken === 'clicked') {
          return {
            gist: [
              { value: 'mandatory_policy', label: 'Mandatory company policy' },
              { value: 'avoid_suspension', label: 'Avoid account suspension' },
              { value: 'hr_requirement', label: 'HR/compliance requirement' },
              { value: 'training_deadline', label: 'Must meet deadline' },
              { value: 'stay_compliant', label: 'Need to stay compliant' }
            ],
            verbatim: [
              { value: 'checked_training_link', label: 'Checked the training/portal link' },
              { value: 'read_deadline', label: 'Read the deadline mentioned' },
              { value: 'verified_contact_info', label: 'Verified contact information provided' },
              { value: 'confirmed_sender', label: 'Confirmed sender name/department' },
              { value: 'read_completion_details', label: 'Read completion requirements' }
            ]
          };
        } else if (actionTaken === 'report') {
          return {
            gist: [
              { value: 'phishing_scare_tactic', label: 'Phishing scare tactic' },
              { value: 'fake_compliance', label: 'Fake compliance email' },
              { value: 'hr_impersonation', label: 'HR/compliance impersonation' },
              { value: 'suspension_threat_fake', label: 'Fake suspension threat' },
              { value: 'protect_colleagues', label: 'Protect colleagues from scam' }
            ],
            verbatim: [
              { value: 'wrong_domain', label: 'Wrong/suspicious sender domain' },
              { value: 'no_such_policy', label: 'No such policy exists (verified)' },
              { value: 'threatening_language', label: 'Threatening language unusual for HR' },
              { value: 'verified_hr_didnt_send', label: 'Verified HR didn\'t send this' },
              { value: 'suspicious_link', label: 'Suspicious link destination' }
            ]
          };
        } else { // ignore
          return {
            gist: [
              { value: 'already_completed', label: 'Already completed this training' },
              { value: 'seems_dubious', label: 'Seems dubious' },
              { value: 'verify_with_hr', label: 'Will verify with HR first' },
              { value: 'low_priority', label: 'Low priority for now' },
              { value: 'uncertain_authenticity', label: 'Uncertain about authenticity' }
            ],
            verbatim: [
              { value: 'completed_recently', label: 'Completed similar training recently' },
              { value: 'unusual_domain', label: 'Unusual sender domain' },
              { value: 'check_official_portal', label: 'Will check official portal instead' },
              { value: 'no_prior_notification', label: 'No prior notification about this' },
              { value: 'verify_legitimacy_first', label: 'Will verify legitimacy before action' }
            ]
          };
        }

      case 'security-alert':
        if (actionTaken === 'engage' || actionTaken === 'clicked') {
          return {
            gist: [
              { value: 'security_threat', label: 'Account security threat' },
              { value: 'verification_needed', label: 'Identity verification needed' },
              { value: 'avoid_lockout', label: 'Avoid account lockout' },
              { value: 'unusual_activity', label: 'Unusual login activity detected' },
              { value: 'it_security_proc', label: 'IT security procedure' }
            ],
            verbatim: [
              { value: 'checked_id_domain', label: 'Checked id.company.com domain' },
              { value: 'read_verification_window', label: 'Read 48-hour/24-hour verification window' },
              { value: 'verified_policy_number', label: 'Verified IT policy number' },
              { value: 'examined_contact', label: 'Examined IT contact details' },
              { value: 'it_sender_confirmed', label: 'Confirmed IT security sender' }
            ]
          };
        } else if (actionTaken === 'report') {
          return {
            gist: [
              { value: 'phishing_alert', label: 'Phishing alert (not real security)' },
              { value: 'fake_security_threat', label: 'Not a real security threat' },
              { value: 'credential_harvesting', label: 'Credential harvesting attempt' },
              { value: 'scare_tactic', label: 'Scare tactic detected' },
              { value: 'it_impersonation', label: 'IT impersonation' }
            ],
            verbatim: [
              { value: 'wrong_domain', label: 'Wrong domain (not official IT domain)' },
              { value: 'no_unusual_activity', label: 'No unusual activity (checked account)' },
              { value: 'suspicious_verification_link', label: 'Suspicious verification link' },
              { value: 'verified_it_didnt_send', label: 'Verified with IT - they didn\'t send this' },
              { value: 'fake_policy_number', label: 'Fake/non-existent policy number' }
            ]
          };
        } else { // ignore
          return {
            gist: [
              { value: 'seems_suspicious', label: 'Seems suspicious' },
              { value: 'verify_directly', label: 'Will verify directly with IT' },
              { value: 'uncertain_legitimacy', label: 'Uncertain about legitimacy' },
              { value: 'no_recent_login', label: 'No recent unusual login by me' },
              { value: 'defer_verification', label: 'Will defer verification' }
            ],
            verbatim: [
              { value: 'unfamiliar_domain', label: 'Unfamiliar security domain' },
              { value: 'check_it_portal', label: 'Will check IT portal directly' },
              { value: 'no_activity_detected', label: 'No unusual activity detected on my end' },
              { value: 'call_it_desk', label: 'Will call IT desk to verify' },
              { value: 'unusual_verification_process', label: 'Unusual verification process' }
            ]
          };
        }

      case 'payroll-document':
        if (actionTaken === 'engage' || actionTaken === 'clicked') {
          return {
            gist: [
              { value: 'tax_form_available', label: 'Tax/payroll document available' },
              { value: 'payroll_communication', label: 'Payroll/HR communication' },
              { value: 'benefits_deadline', label: 'Benefits/filing deadline approaching' },
              { value: 'employee_requirement', label: 'Employee requirement' },
              { value: 'need_document', label: 'Need this document' }
            ],
            verbatim: [
              { value: 'verified_sender', label: 'Verified HR/payroll sender' },
              { value: 'checked_timing', label: 'Timing appropriate (enrollment/tax season)' },
              { value: 'examined_portal_url', label: 'Examined portal URL destination' },
              { value: 'verified_contact_info', label: 'Verified contact information' },
              { value: 'confirmed_deadline', label: 'Confirmed deadline mentioned' }
            ]
          };
        } else if (actionTaken === 'report') {
          return {
            gist: [
              { value: 'payroll_phishing', label: 'Payroll/benefits phishing scam' },
              { value: 'identity_theft_attempt', label: 'Identity theft attempt' },
              { value: 'payroll_impersonation', label: 'Payroll/HR impersonation' },
              { value: 'data_harvesting', label: 'Personal data harvesting attempt' },
              { value: 'benefits_scam', label: 'Benefits/bonus scam' }
            ],
            verbatim: [
              { value: 'wrong_domain', label: 'Wrong/suspicious sender domain' },
              { value: 'personal_data_request', label: 'Suspicious personal data request' },
              { value: 'suspicious_portal_url', label: 'Suspicious portal URL' },
              { value: 'verified_payroll_didnt_send', label: 'Verified payroll didn\'t send this' },
              { value: 'unusual_urgency', label: 'Unusual urgency/expiration threat' }
            ]
          };
        } else { // ignore
          return {
            gist: [
              { value: 'already_have_document', label: 'Already received this document' },
              { value: 'seems_suspicious', label: 'Seems suspicious' },
              { value: 'verify_with_payroll', label: 'Will verify with payroll/HR' },
              { value: 'not_urgent', label: 'Not urgent for me now' },
              { value: 'uncertain_legitimacy', label: 'Uncertain about legitimacy' }
            ],
            verbatim: [
              { value: 'already_received', label: 'Already received document elsewhere' },
              { value: 'unusual_domain', label: 'Unusual HR/payroll domain' },
              { value: 'check_official_portal', label: 'Will check official portal' },
              { value: 'no_prior_notification', label: 'No prior notification about this' },
              { value: 'verify_directly', label: 'Will verify directly with HR/payroll' }
            ]
          };
        }

      default:
        // Fallback to generic options if no category specified
        return {
          gist: [
            { value: 'looked_legitimate', label: 'It looked legitimate overall' },
            { value: 'looked_suspicious', label: 'It looked suspicious overall' },
            { value: 'gut_feeling', label: 'I had a gut feeling about it' },
            { value: 'seemed_routine', label: 'It seemed routine or unimportant' },
            { value: 'required_action', label: 'It required some action from me' }
          ],
          verbatim: [
            { value: 'checked_sender', label: 'The sender address details' },
            { value: 'checked_url', label: 'The link destination' },
            { value: 'checked_domain', label: 'The domain name' },
            { value: 'checked_grammar', label: 'Grammar, spelling, or formatting' },
            { value: 'checked_content', label: 'Specific content details' }
          ]
        };
    }
  };

  // Email primary reasons (radio buttons)
  const emailPrimaryReasons = getEmailPrimaryReasons();

  // Coding primary reasons - SCENARIO-AWARE based on code source type
  const getCodingPrimaryReasons = () => {
    // Default options for scenarios without specific types
    const defaultOptions = actionTaken === 'proceed' ? {
      gist: [
        { value: 'looked_fine', label: 'Looked fine' },
        { value: 'need_it', label: 'I need this functionality' },
        { value: 'trust_source', label: 'I trust the source' },
        { value: 'seems_standard', label: 'Seems like standard code' },
        { value: 'felt_safe', label: 'Felt safe to use' }
      ],
      verbatim: [
        { value: 'checked_code', label: 'I reviewed the code details' },
        { value: 'recognize_pattern', label: 'I recognize this pattern' },
        { value: 'verified_source', label: 'I verified the source or author' },
        { value: 'no_red_flags', label: 'No security red flags identified' },
        { value: 'matches_docs', label: 'Matches official documentation' }
      ]
    } : {
      gist: [
        { value: 'looked_suspicious', label: 'Looked suspicious' },
        { value: 'dont_need', label: 'I do not need this' },
        { value: 'dont_trust', label: 'I do not trust the source' },
        { value: 'seems_risky', label: 'Seems risky' },
        { value: 'felt_wrong', label: 'Something felt wrong' }
      ],
      verbatim: [
        { value: 'found_issue', label: 'Found a specific issue' },
        { value: 'unknown_source', label: 'Unknown or untrusted source' },
        { value: 'security_concern', label: 'Security concern identified' },
        { value: 'saw_warning', label: 'A warning sign appeared' },
        { value: 'policy_violation', label: 'Action conflicts with company policy' }
      ]
    };

    // Scenario-specific options based on code source type
    switch (scenarioType) {
      case 'ai-suggestion':
        // GitHub Copilot, ChatGPT - AI-generated code suggestions
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'ai_recommendation', label: 'AI recommendation (Copilot/ChatGPT)' },
            { value: 'code_works', label: 'Code works as expected' },
            { value: 'clean_implementation', label: 'Clean implementation' },
            { value: 'need_functionality', label: 'Need this functionality' },
            { value: 'trust_ai_source', label: 'Trust AI source (GitHub/OpenAI)' }
          ],
          verbatim: [
            { value: 'ai_autocomplete', label: 'AI autocompleted the code' },
            { value: 'syntax_correct', label: 'Code syntax looks correct' },
            { value: 'tested_works', label: 'Tested and it works' },
            { value: 'from_github_openai', label: 'From GitHub/OpenAI AI' },
            { value: 'no_obvious_errors', label: 'No obvious errors in code' }
          ]
        } : {
          gist: [
            { value: 'ai_hallucination_risk', label: 'AI hallucination risk' },
            { value: 'security_vulnerability', label: 'Security vulnerability concern' },
            { value: 'untrusted_ai', label: 'Untrusted AI suggestion' },
            { value: 'too_risky_ai', label: 'Too risky to use' },
            { value: 'ai_generated_bug', label: 'AI-generated bug/vulnerability' }
          ],
          verbatim: [
            { value: 'package_not_exists', label: 'Package doesn\'t exist on npm' },
            { value: 'sql_injection', label: 'String concatenation in SQL (injection)' },
            { value: 'data_exfiltration', label: 'External data upload detected' },
            { value: 'no_input_validation', label: 'No input validation/sanitization' },
            { value: 'clipboard_monitoring', label: 'Clipboard monitoring detected' }
          ]
        };

      case 'package':
        // npm packages - lodash, typosquatting packages
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'popular_package', label: 'Popular package' },
            { value: 'high_downloads', label: 'High download count' },
            { value: 'colleague_recommended', label: 'Colleague recommended' },
            { value: 'well_known_library', label: 'Well-known library' },
            { value: 'needed_utilities', label: 'Needed utility functions' }
          ],
          verbatim: [
            { value: 'verified_npm', label: 'Verified on npm registry' },
            { value: 'checked_downloads', label: 'Checked download count (50M+)' },
            { value: 'official_package', label: 'Confirmed official package' },
            { value: 'mit_license', label: 'MIT license verified' },
            { value: 'active_maintenance', label: 'Active maintenance confirmed' }
          ]
        } : {
          gist: [
            { value: 'typosquatting_suspected', label: 'Typosquatting suspected' },
            { value: 'low_downloads', label: 'Low download count' },
            { value: 'misspelled_name', label: 'Misspelled package name' },
            { value: 'suspicious_package', label: 'Suspicious package' },
            { value: 'unknown_package', label: 'Unknown package' }
          ],
          verbatim: [
            { value: 'name_typo', label: 'Package name typo (e.g., "extanded")' },
            { value: 'postinstall_script', label: 'Suspicious postinstall script found' },
            { value: 'low_download_count', label: 'Very low download count' },
            { value: 'not_in_npm', label: 'Not found in npm registry' },
            { value: 'telemetry_script', label: 'telemetry.js or tracking script' }
          ]
        };

      case 'snippet':
        // Stack Overflow, official docs - code snippets
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'official_docs', label: 'Official documentation source' },
            { value: 'trusted_source', label: 'Trusted source' },
            { value: 'standard_pattern', label: 'Standard coding pattern' },
            { value: 'clean_example', label: 'Clean code example' },
            { value: 'recognized_pattern', label: 'Recognized this pattern' }
          ],
          verbatim: [
            { value: 'from_official_docs', label: 'From react.dev/official docs' },
            { value: 'proper_cleanup', label: 'Proper cleanup implemented' },
            { value: 'abortcontroller_usage', label: 'AbortController usage verified' },
            { value: 'dependencies_correct', label: 'Effect dependencies correct' },
            { value: 'high_upvotes', label: 'High upvotes on answer' }
          ]
        } : {
          gist: [
            { value: 'anonymous_answer', label: 'Anonymous/low-rep answer' },
            { value: 'unknown_package_snippet', label: 'Unknown package referenced' },
            { value: 'suspicious_endpoint', label: 'Suspicious endpoint/URL' },
            { value: 'low_reputation', label: 'Low reputation source' },
            { value: 'mining_detected', label: 'Mining/malicious activity detected' }
          ],
          verbatim: [
            { value: 'zero_upvotes', label: '0 upvotes on answer' },
            { value: 'mining_url', label: 'mining-service.xyz or suspicious URL' },
            { value: 'package_not_npm', label: 'Package not on npm registry' },
            { value: 'websocket_unknown', label: 'WebSocket to unknown service' },
            { value: 'no_verification', label: 'No way to verify authenticity' }
          ]
        };

      case 'tool':
        // VS Code extensions, npm audit, security tools
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'company_requirement', label: 'Company requirement/policy' },
            { value: 'official_tool', label: 'Official tool' },
            { value: 'security_audit', label: 'Security audit purpose' },
            { value: 'high_ratings', label: 'High ratings/reviews' },
            { value: 'trusted_marketplace', label: 'Trusted marketplace' }
          ],
          verbatim: [
            { value: 'verified_company_policy', label: 'Verified company policy requirement' },
            { value: 'official_npm_command', label: 'Official npm command' },
            { value: 'company_namespace', label: '@company namespace verified' },
            { value: 'internal_github', label: 'Internal GitHub repository' },
            { value: 'it_requirement', label: 'IT department requirement' }
          ]
        } : {
          gist: [
            { value: 'data_exfiltration_risk', label: 'Data exfiltration risk' },
            { value: 'not_official_version', label: 'Not official version' },
            { value: 'suspicious_permissions', label: 'Suspicious permissions requested' },
            { value: 'fake_extension', label: 'Fake/imposter extension' },
            { value: 'privacy_violation', label: 'Privacy violation' }
          ],
          verbatim: [
            { value: 'not_official_prettier', label: 'NOT official Prettier extension' },
            { value: 'uploads_code', label: 'Uploads code content to server' },
            { value: 'external_analytics', label: 'analytics-prettier.com (external domain)' },
            { value: 'gettext_detected', label: 'doc.getText() code detected' },
            { value: 'no_publisher_verification', label: 'Publisher not verified' }
          ]
        };

      case 'library':
      case 'api':
        // Use default options for these less common types
        return defaultOptions;

      default:
        return defaultOptions;
    }
  };

  const codingPrimaryReasons = getCodingPrimaryReasons();

  // Device primary reasons - SCENARIO-AWARE based on visual type
  const getDevicePrimaryReasons = () => {
    // Default options for scenarios without specific visual types
    const defaultOptions = actionTaken === 'proceed' ? {
      gist: [
        { value: 'felt_appropriate', label: 'Proceeding felt appropriate overall' },
        { value: 'source_seemed_trustworthy', label: 'The source seemed trustworthy' },
        { value: 'expected_in_context', label: 'The situation matched what I expected' },
        { value: 'benefit_outweighed_concern', label: 'The benefit seemed to outweigh the concern' },
        { value: 'familiar_safe_pattern', label: 'It matched a familiar, safe pattern' }
      ],
      verbatim: [
        { value: 'checked_displayed_source', label: 'I checked the displayed source, sender, or domain' },
        { value: 'read_warning_details', label: 'I read the warning and technical details' },
        { value: 'matched_timing_context', label: 'The timing and situation matched the stated context' },
        { value: 'recognized_device_account', label: 'I recognized the device, account, or application' },
        { value: 'checked_policy_or_reference', label: 'I checked the policy, ticket, or reference shown' }
      ]
    } : {
      gist: [
        { value: 'felt_risky', label: 'The situation felt risky overall' },
        { value: 'source_seemed_untrustworthy', label: 'The source seemed untrustworthy' },
        { value: 'unexpected_request', label: 'The request was unexpected' },
        { value: 'concern_outweighed_benefit', label: 'The concern outweighed the benefit' },
        { value: 'familiar_threat_pattern', label: 'It matched a familiar threat pattern' }
      ],
      verbatim: [
        { value: 'source_or_domain_mismatch', label: 'The displayed source, sender, or domain did not match' },
        { value: 'warning_or_permission_concern', label: 'A warning, permission, or access request concerned me' },
        { value: 'could_not_verify_context', label: 'I could not verify the request from the information shown' },
        { value: 'device_account_mismatch', label: 'The device, account, location, or timing did not match' },
        { value: 'policy_or_sensitive_access', label: 'The action conflicted with policy or involved sensitive access' }
      ]
    };

    // Broad, action-specific reasons are intentionally used for the live
    // phishing/device study. The legacy visual-type lists below assumed facts
    // that were absent from many scenarios (for example, treating every system
    // notification as an OS update). They remain available only for an explicit
    // future configuration after scenario-level validation.
    if (!config?.useTypeSpecificDeviceReasons) {
      return defaultOptions;
    }

    // Scenario-specific options based on visual type
    switch (scenarioType) {
      case 'software-install':
        // IT Scanner, Security Tool - software installation prompts
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'from_it', label: 'Came from IT department' },
            { value: 'looks_professional', label: 'Looks professional' },
            { value: 'expected_software', label: 'Expected this software' },
            { value: 'trust_source', label: 'Trust the source' },
            { value: 'seems_legitimate', label: 'Seems legitimate' }
          ],
          verbatim: [
            { value: 'checked_publisher', label: 'Verified publisher name' },
            { value: 'checked_ticket', label: 'Checked ticket/reference number' },
            { value: 'verified_internal', label: 'Verified internal source' },
            { value: 'checked_certificate', label: 'Code signing certificate' },
            { value: 'confirmed_with_it', label: 'Could confirm with IT' }
          ]
        } : {
          gist: [
            { value: 'suspicious_request', label: 'Request seemed suspicious' },
            { value: 'dont_trust', label: 'Do not trust this' },
            { value: 'unexpected', label: 'Was unexpected' },
            { value: 'too_risky', label: 'Too risky' },
            { value: 'felt_wrong', label: 'Something felt wrong' }
          ],
          verbatim: [
            { value: 'no_ticket', label: 'No ticket/reference number' },
            { value: 'unknown_publisher', label: 'Unknown publisher' },
            { value: 'no_verification', label: 'Could not verify source' },
            { value: 'certificate_issue', label: 'Certificate problem' },
            { value: 'it_not_mentioned', label: 'IT did not mention this' }
          ]
        };

      case 'antivirus-alert':
        // FREE_MOVIES - antivirus warnings
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'false_positive', label: 'Thought it was false positive' },
            { value: 'really_wanted', label: 'Really wanted this file' },
            { value: 'trust_source', label: 'Trust the source' },
            { value: 'seemed_ok', label: 'Seemed okay to me' },
            { value: 'not_real_threat', label: 'Did not seem like real threat' }
          ],
          verbatim: [
            { value: 'checked_threat_name', label: 'Looked up threat name' },
            { value: 'checked_file_size', label: 'File size seemed normal' },
            { value: 'source_known', label: 'Source was known to me' },
            { value: 'warning_details', label: 'Warning details checked out' },
            { value: 'researched_file', label: 'Researched the file type' }
          ]
        } : {
          gist: [
            { value: 'malware_detected', label: 'Malware was detected' },
            { value: 'too_risky', label: 'Too risky' },
            { value: 'trust_antivirus', label: 'Trust my antivirus' },
            { value: 'suspicious_file', label: 'File seemed suspicious' },
            { value: 'not_worth_risk', label: 'Not worth the risk' }
          ],
          verbatim: [
            { value: 'saw_trojan_name', label: 'Saw trojan/malware name' },
            { value: 'foreign_source', label: 'Foreign/untrusted source' },
            { value: 'severe_warning', label: 'Severity level was high' },
            { value: 'checked_threat_type', label: 'Checked threat type details' },
            { value: 'file_extension', label: 'Executable file extension' }
          ]
        };

      case 'email-attachment-preview':
        // Excel attachment - email attachments
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'colleague_sent', label: 'From a colleague' },
            { value: 'expected_document', label: 'Expected this document' },
            { value: 'work_related', label: 'Work-related file' },
            { value: 'looks_normal', label: 'Looks normal' },
            { value: 'trust_sender', label: 'Trust the sender' }
          ],
          verbatim: [
            { value: 'verified_sender_domain', label: 'Verified sender domain' },
            { value: 'checked_sender_name', label: 'Confirmed sender identity' },
            { value: 'file_type_appropriate', label: 'File type appropriate' },
            { value: 'subject_makes_sense', label: 'Subject line makes sense' },
            { value: 'can_verify_sender', label: 'Can verify with sender' }
          ]
        } : {
          gist: [
            { value: 'suspicious_attachment', label: 'Attachment seemed suspicious' },
            { value: 'unexpected', label: 'Was unexpected' },
            { value: 'dont_trust_sender', label: 'Do not trust sender' },
            { value: 'too_risky', label: 'Too risky to open' },
            { value: 'felt_wrong', label: 'Something felt wrong' }
          ],
          verbatim: [
            { value: 'domain_mismatch', label: 'Domain did not match' },
            { value: 'unexpected_attachment', label: 'Unexpected attachment type' },
            { value: 'unknown_sender', label: 'Sender not recognized' },
            { value: 'suspicious_filename', label: 'Suspicious filename' },
            { value: 'cannot_verify', label: 'Cannot verify sender' }
          ]
        };

      case 'system-notification':
        // System Update - OS/system notifications
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'system_update', label: 'Routine system update' },
            { value: 'looks_official', label: 'Looks official' },
            { value: 'expected_update', label: 'Expected update' },
            { value: 'normal_notification', label: 'Normal notification' },
            { value: 'trust_system', label: 'Trust my system' }
          ],
          verbatim: [
            { value: 'system_settings', label: 'From system settings' },
            { value: 'verified_legitimate', label: 'Verified it was legitimate' },
            { value: 'scheduled_update', label: 'Part of scheduled updates' },
            { value: 'system_ui', label: 'Matches system UI' },
            { value: 'no_red_flags', label: 'No red flags found' }
          ]
        } : {
          gist: [
            { value: 'suspicious_popup', label: 'Popup seemed suspicious' },
            { value: 'unexpected', label: 'Was unexpected' },
            { value: 'seems_fake', label: 'Seems fake' },
            { value: 'too_urgent', label: 'Too urgent/pushy' },
            { value: 'not_real', label: 'Did not seem real' }
          ],
          verbatim: [
            { value: 'browser_not_system', label: 'From browser, not system' },
            { value: 'wrong_ui', label: 'UI did not match system' },
            { value: 'checked_settings', label: 'Checked actual system settings' },
            { value: 'unusual_request', label: 'Unusual request' },
            { value: 'verified_fake', label: 'Verified it was fake' }
          ]
        };

      case 'vscode-extension':
        // Browser/VS Code extensions
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'helpful_tool', label: 'Helpful tool' },
            { value: 'popular_extension', label: 'Popular extension' },
            { value: 'looks_legitimate', label: 'Looks legitimate' },
            { value: 'need_functionality', label: 'Need this functionality' },
            { value: 'trust_marketplace', label: 'Trust the marketplace' }
          ],
          verbatim: [
            { value: 'verified_publisher', label: 'Verified publisher/developer' },
            { value: 'high_user_count', label: 'High user count/downloads' },
            { value: 'reputable_company', label: 'Used by reputable companies' },
            { value: 'verified_badge', label: 'Verified developer badge' },
            { value: 'positive_reviews', label: 'Positive reviews/ratings' }
          ]
        } : {
          gist: [
            { value: 'suspicious_extension', label: 'Extension seemed suspicious' },
            { value: 'dont_need', label: 'Do not need this' },
            { value: 'too_risky', label: 'Too risky' },
            { value: 'dont_trust', label: 'Do not trust it' },
            { value: 'seems_fake', label: 'Seems fake/imposter' }
          ],
          verbatim: [
            { value: 'unknown_publisher', label: 'Unknown/suspicious publisher' },
            { value: 'low_user_count', label: 'Low user count compared to similar' },
            { value: 'excessive_permissions', label: 'Requests excessive permissions' },
            { value: 'name_copycat', label: 'Name similar to legitimate extension' },
            { value: 'unverified_developer', label: 'Unverified/new developer' }
          ]
        };

      case 'file-download-prompt':
        // Security Tool download - browser download prompts
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'needed_tool', label: 'Needed this tool' },
            { value: 'looks_legitimate', label: 'Looks legitimate' },
            { value: 'trust_website', label: 'Trust the website' },
            { value: 'professional_looking', label: 'Professional looking' },
            { value: 'seems_safe', label: 'Seems safe' }
          ],
          verbatim: [
            { value: 'checked_url', label: 'Verified URL/domain' },
            { value: 'https_secure', label: 'HTTPS secure connection' },
            { value: 'file_type_ok', label: 'File type appropriate' },
            { value: 'official_site', label: 'From official site' },
            { value: 'verified_source', label: 'Verified the source' }
          ]
        } : {
          gist: [
            { value: 'suspicious_download', label: 'Download seemed suspicious' },
            { value: 'dont_trust_site', label: 'Do not trust site' },
            { value: 'too_risky', label: 'Too risky' },
            { value: 'dont_need', label: 'Do not need this' },
            { value: 'seems_malicious', label: 'Seems malicious' }
          ],
          verbatim: [
            { value: 'wrong_domain', label: 'Wrong/suspicious domain' },
            { value: 'no_https', label: 'Not HTTPS secure' },
            { value: 'suspicious_filename', label: 'Suspicious filename' },
            { value: 'unofficial_site', label: 'Unofficial website' },
            { value: 'verified_threat', label: 'Verified it was a threat' }
          ]
        };

      case 'network-selection':
        // WiFi network selection
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'expected_wifi_name', label: 'Expected WiFi network name' },
            { value: 'strong_signal', label: 'Strong signal strength' },
            { value: 'recommended_badge', label: 'RECOMMENDED badge shown' },
            { value: 'wifi_urgency', label: 'Needed WiFi urgently' },
            { value: 'secure_network', label: 'Secure network (WPA2)' }
          ],
          verbatim: [
            { value: 'checked_network_name', label: 'Checked network name details' },
            { value: 'verified_encryption', label: 'Verified encryption type' },
            { value: 'saw_recommended', label: 'Saw RECOMMENDED badge' },
            { value: 'signal_strength', label: 'Strong signal strength' },
            { value: 'secure_in_name', label: 'Network name included "Secure"' }
          ]
        } : {
          gist: [
            { value: 'suspicious_wifi', label: 'Suspicious WiFi name' },
            { value: 'wrong_location', label: 'Wrong location for this network' },
            { value: 'too_convenient', label: 'Too convenient to be real' },
            { value: 'it_policy_violation', label: 'IT policy violation' },
            { value: 'evil_twin_attack', label: 'Possible evil twin attack' }
          ],
          verbatim: [
            { value: 'location_mismatch', label: 'Location does not match network' },
            { value: 'name_slight_difference', label: 'Name slightly different from real' },
            { value: 'public_location', label: 'Public location (not trusted)' },
            { value: 'it_policy_vpn', label: 'IT policy requires VPN' },
            { value: 'evil_twin_common', label: 'Evil twin common in public spaces' }
          ]
        };

      case 'mfa-notifications':
        // MFA Push Fatigue - repeated authentication notifications
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'wanted_stop_alerts', label: 'Wanted notifications to stop' },
            { value: 'exhausted_frustrated', label: 'Exhausted and frustrated' },
            { value: 'approve_to_stop', label: 'Message said approve to stop alerts' },
            { value: 'inconvenient_timing', label: 'Inconvenient timing' },
            { value: 'harassment_overwhelming', label: 'Notifications were overwhelming' }
          ],
          verbatim: [
            { value: 'read_message', label: 'Read the notification message' },
            { value: 'counted_notifications', label: 'Saw multiple repeated notifications' },
            { value: 'noticed_timing', label: 'Noticed the timing' },
            { value: 'authenticator_source', label: 'Checked notification source' },
            { value: 'repeated_pattern', label: 'Noticed repeated pattern' }
          ]
        } : {
          gist: [
            { value: 'foreign_location_sus', label: 'Foreign/unknown location suspicious' },
            { value: 'no_login_made', label: 'I did not make this login attempt' },
            { value: 'mfa_fatigue_recognized', label: 'MFA fatigue attack recognized' },
            { value: 'too_risky_approve', label: 'Too risky to approve' },
            { value: 'it_warned_this', label: 'IT warned about this' }
          ],
          verbatim: [
            { value: 'foreign_location', label: 'Foreign/unknown location shown' },
            { value: 'suspicious_ip', label: 'Suspicious IP address' },
            { value: 'wrong_device_type', label: 'Wrong device type shown' },
            { value: 'confirmed_no_login', label: 'Confirmed I made no login attempt' },
            { value: 'remembered_it_warning', label: 'Remembered IT security warnings' }
          ]
        };

      case 'usb-dialog':
        // USB device connection prompts
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'brand_looked_genuine', label: 'Brand/device looked genuine' },
            { value: 'colleagues_using', label: 'Colleagues were using it' },
            { value: 'urgency_needed', label: 'Needed device urgently' },
            { value: 'note_said_safe', label: 'Note/label said it\'s safe' },
            { value: 'convenient_solution', label: 'Convenient solution' }
          ],
          verbatim: [
            { value: 'verified_branding', label: 'Verified brand/logo details' },
            { value: 'saw_colleagues_use', label: 'Saw colleagues using similar' },
            { value: 'checked_condition', label: 'Checked device condition' },
            { value: 'read_label', label: 'Read attached label/note' },
            { value: 'examined_appearance', label: 'Examined device appearance' }
          ]
        } : {
          gist: [
            { value: 'unknown_device_owner', label: 'Unknown device owner' },
            { value: 'usb_security_risk', label: 'USB/device security risk' },
            { value: 'sensitive_data', label: 'Device has sensitive data' },
            { value: 'policy_violation_usb', label: 'IT policy violation' },
            { value: 'malicious_device_threat', label: 'Possible malicious device' }
          ],
          verbatim: [
            { value: 'no_asset_tag', label: 'No IT asset tag' },
            { value: 'unknown_owner_source', label: 'Unknown owner/source' },
            { value: 'it_policy_no_unknown', label: 'IT policy: no unknown devices' },
            { value: 'malicious_devices_exist', label: 'Malicious USB devices exist' },
            { value: 'company_data_access', label: 'Device has company data access' }
          ]
        };

      case 'browser-warning':
        // VPN access, router firmware updates - browser warnings/redirects
        return actionTaken === 'proceed' ? {
          gist: [
            { value: 'need_access', label: 'Need access to work resources' },
            { value: 'company_requirement', label: 'Company requirement' },
            { value: 'looks_legitimate', label: 'Looks legitimate' },
            { value: 'security_update', label: 'Security update needed' },
            { value: 'normal_notification', label: 'Normal notification' }
          ],
          verbatim: [
            { value: 'checked_domain', label: 'Checked domain/URL' },
            { value: 'verified_company_policy', label: 'Verified company policy' },
            { value: 'it_sent_instructions', label: 'IT sent instructions' },
            { value: 'expected_behavior', label: 'Expected behavior' },
            { value: 'official_gateway', label: 'Official company gateway' }
          ]
        } : {
          gist: [
            { value: 'suspicious_redirect', label: 'Suspicious browser redirect' },
            { value: 'popup_warning', label: 'Unexpected popup' },
            { value: 'firmware_scam', label: 'Firmware update scam' },
            { value: 'too_risky', label: 'Too risky' },
            { value: 'dns_hijacking', label: 'DNS hijacking suspected' }
          ],
          verbatim: [
            { value: 'browser_redirect', label: 'Browser redirect (not admin panel)' },
            { value: 'wrong_domain', label: 'Wrong domain (not official)' },
            { value: 'router_updates_admin', label: 'Router updates via admin panel only' },
            { value: 'checked_url_mismatch', label: 'Checked URL - mismatch found' },
            { value: 'never_popup_firmware', label: 'Routers never popup firmware updates' }
          ]
        };

      default:
        // Fallback to default options for older scenarios without visualData
        return defaultOptions;
    }
  };

  const devicePrimaryReasons = getDevicePrimaryReasons();


  const primaryReasons = taskType === 'device' ? devicePrimaryReasons :
                        taskType === 'coding' ? codingPrimaryReasons :
                        emailPrimaryReasons;

  // Merge gist + verbatim into one list, Fisher-Yates shuffle, store on mount
  useEffect(() => {
    const gistWithCategory = primaryReasons.gist.map(r => ({ ...r, category: 'gist' as const }));
    const verbatimWithCategory = primaryReasons.verbatim.map(r => ({ ...r, category: 'verbatim' as const }));
    const combined = [...gistWithCategory, ...verbatimWithCategory];
    const shuffled = fisherYatesShuffle(combined);
    setShuffledList(shuffled);
  }, [actionTaken, taskType]); // Re-shuffle if context changes

  const getQuestionText = () => {
    if (taskType === 'email') {
      switch (actionTaken) {
        case 'engage':
          return "What made you decide to click/respond to this email?";
        case 'verify':
          return "What made you decide to verify this email before acting?";
        case 'ignore':
          return "What made you decide to ignore this email?";
        case 'report':
          return "What made you decide to report this email?";
        case 'clicked':
          return "What made you decide to click this link?";
        default:
          return "What made you decide not to click this link?";
      }
    } else {
      return actionTaken === 'proceed'
        ? "What made you decide to proceed with this action?"
        : "What made you decide to decline this action?";
    }
  };

  // Handle rank input: type 1, 2, or 3 next to an option
  const handleRankInput = (optionValue: string, inputVal: string) => {
    const newMap = { ...rankMap };

    if (inputVal === '' || inputVal === ' ') {
      // Clearing the rank
      delete newMap[optionValue];
      setRankMap(newMap);
      return;
    }

    const rank = parseInt(inputVal);
    if (isNaN(rank) || rank < 1 || rank > 3) return; // Only accept 1, 2, 3

    // If another option already has this rank, clear it (auto-deduplicate)
    for (const key of Object.keys(newMap)) {
      if (newMap[key] === rank) {
        delete newMap[key];
      }
    }

    newMap[optionValue] = rank;
    setRankMap(newMap);
  };

  // Build ranked reasons from rankMap
  const getRankedReasons = (): RankedReason[] => {
    if (!shuffledList) return [];
    const ranked: RankedReason[] = [];
    for (const [value, rank] of Object.entries(rankMap)) {
      const option = shuffledList.find(o => o.value === value);
      if (option) {
        ranked.push({ rank, value, label: option.label, category: option.category });
      }
    }
    return ranked.sort((a, b) => a.rank - b.rank);
  };

  // A ranked primary reason OR a participant-written reason is sufficient.
  // Ranks 2 and 3 remain optional so the interface never forces artifact reasons.
  const hasMinimumResponse = (): boolean => {
    const ranks = Object.values(rankMap);
    return ranks.includes(1) || customReason.trim().length > 0;
  };
  // Keep the old name as an alias so we don't break any callers outside this file
  const hasAllThreeRanks = hasMinimumResponse;

  // Calculate gist dominance score — adapts to 1, 2, or 3 ranked reasons
  const calculateGistDominanceScore = (ranked: RankedReason[]): number => {
    const weights: Record<number, number> = { 1: 3, 2: 2, 3: 1 };
    let gistPoints = 0;
    let maxPoints = 0;
    ranked.forEach(r => {
      const w = weights[r.rank] || 0;
      maxPoints += w;
      if (r.category === 'gist') {
        gistPoints += w;
      }
    });
    return maxPoints > 0 ? gistPoints / maxPoints : 0;
  };

  const handleNextStep = () => {
    if (currentStep === 1 && hasAllThreeRanks()) {
      setStep1CompletedTime(Date.now());
      setCurrentStep(2);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const rankedReasons = getRankedReasons();
    console.log('[MicroSurvey] handleSubmit called');
    console.log('[MicroSurvey] rankedReasons:', rankedReasons);

    if (!hasMinimumResponse()) {
      console.log('[MicroSurvey] Need a ranked reason or a written reason, returning');
      return;
    }

    const rank1 = rankedReasons[0]; // undefined when the participant uses custom text only
    // A custom-text-only response has not been coded as gist or verbatim yet;
    // leave the score missing rather than recording 0 (which would falsely
    // mean fully verbatim).
    const gistDominanceScore = rankedReasons.length > 0
      ? calculateGistDominanceScore(rankedReasons)
      : undefined;
    const decisionLatency = Date.now() - selectionStartTime;
    const totalMicroSurveyTime = Date.now() - microSurveyStartTime;
    const step1Time = step1CompletedTime ? (step1CompletedTime - microSurveyStartTime) : null;
    const step2Time = step1CompletedTime ? (Date.now() - step1CompletedTime) : null;

    // Calculate selected indices within the shuffled list
    const selectedIndices = rankedReasons.map(r =>
      shuffledList ? shuffledList.findIndex(opt => opt.value === r.value) : -1
    );

    // Presented order (full shuffled list values)
    const presentedOrder = shuffledList ? shuffledList.map(opt => opt.value) : [];

    console.log('[MicroSurvey] Gist dominance score:', gistDominanceScore);
    console.log('[MicroSurvey] Rank 1 (legacy reason):', rank1?.value, rank1?.category);
    console.log('[MicroSurvey] Decision latency (ms):', decisionLatency);
    console.log('[MicroSurvey] Presented order:', presentedOrder);
    console.log('[MicroSurvey] Selected indices:', selectedIndices);

    if (onSubmit) {
      console.log('[MicroSurvey] Calling onSubmit callback');
      onSubmit({
        // New ranking fields
        rankedReasons: rankedReasons.map(r => ({
          rank: r.rank,
          value: r.value,
          label: r.label,
          category: r.category
        })),
        gistDominanceScore,

        // Legacy fields (populated from rank 1 for backward compat)
        reason: rank1?.value || 'custom_reason',
        customReason,
        reasonCategory: rank1?.category || 'other',
        primaryReasonKeySelected: rank1?.value,
        routeSelected: rank1?.category,

        // Position bias fields (updated for single list)
        presentedOrder,
        selectedIndices,

        // Legacy position bias fields (approximate backward compat)
        gistOrder: shuffledList ? shuffledList.filter(o => o.category === 'gist').map(o => o.value) : [],
        verbatimOrder: shuffledList ? shuffledList.filter(o => o.category === 'verbatim').map(o => o.value) : [],
        selectedIndexWithinColumn: selectedIndices[0],

        // Timing and perceived state
        confidence,
        metacognitiveCertainty,
        perceivedTimePressure,
        decisionLatencyMs: decisionLatency,
        totalMicroSurveyTimeMs: totalMicroSurveyTime,
        step1TimeMs: step1Time,
        step2TimeMs: step2Time
      });
    } else if (onResponse) {
      console.log('[MicroSurvey] Calling onResponse callback');
      onResponse(rank1?.value || customReason || 'no_reason');
    } else {
      console.log('[MicroSurvey] WARNING: No callback provided!');
    }
  };


  return (
    <div className="modal-overlay" onClick={effectiveOnClose}>
      <div className="modal micro-survey-clean" data-action={actionTaken} onClick={e => e.stopPropagation()}>
        {currentStep === 1 ? (
          <>
            <h3 className="modal-title">{getQuestionText()}</h3>

            <form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }} className="survey-form">
              {/* Ranking Section */}
              <div className="primary-reason-section">
                <p className="section-instruction">
                  Rank up to three reasons (1 = most important). Rank 1 is required unless you write your own reason below.
                </p>

                <div className="ranking-list">
                  {shuffledList ? shuffledList.map(option => {
                    const rank = rankMap[option.value];
                    return (
                      <div
                        key={option.value}
                        className={`ranking-option ${rank ? 'ranked' : 'unranked'}`}
                      >
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          className="rank-input"
                          value={rank != null ? String(rank) : ''}
                          onChange={(e) => handleRankInput(option.value, e.target.value)}
                          placeholder="—"
                        />
                        <span className="ranking-option-label">{option.label}</span>
                      </div>
                    );
                  }) : (
                    <div className="loading-options">Loading options...</div>
                  )}
                </div>
              </div>

              {/* Optional typing box */}
              <div className="custom-reason-section">
                <p className="section-instruction">Or describe your own reason:</p>
                <textarea
                  className="custom-reason-input"
                  placeholder="Type your reason here (optional)"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="submit"
                  className="btn btn-primary clean"
                  disabled={!hasAllThreeRanks()}
                >
                  Next
                </button>
                <button type="button" className="btn btn-secondary clean" onClick={effectiveOnClose}>
                  Cancel
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h3 className="modal-title">How confident are you in your reason or ranking?</h3>

            <form onSubmit={handleSubmit} className="survey-form">
              {/* Show all 3 ranked reasons */}
              <div className="selected-reason-recap">
                {getRankedReasons().length > 0 ? (
                  <>
                    <p className="recap-label">Your ranked reasons:</p>
                    <div className="recap-ranked-list">
                      {getRankedReasons().map(r => (
                        <div key={r.value} className="recap-ranked-item">
                          <span className="recap-rank-number">{r.rank}</span>
                          <span className="recap-rank-label">{r.label}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="recap-label">Your written reason:</p>
                    <p>{customReason}</p>
                  </>
                )}
              </div>

              {/* Confidence Slider Section */}
              <div className="confidence-section">
                <div className="confidence-slider-container">
                  <div className="slider-labels">
                    <span>Not confident</span>
                    <span>Very confident</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={confidence}
                    onChange={(e) => setConfidence(parseInt(e.target.value))}
                    className="confidence-slider clean"
                  />
                  <div className="confidence-value">{confidence}%</div>
                </div>
              </div>

              {/* Metacognitive Certainty Slider — discriminates gist from careless */}
              <div className="confidence-section">
                <p className="section-instruction" style={{ marginBottom: '0.3rem', fontSize: '0.85rem' }}>How certain are you about <strong>WHY</strong> you made this choice?</p>
                <div className="confidence-slider-container">
                  <div className="slider-labels">
                    <span>Can't explain why</span>
                    <span>Know exactly why</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={metacognitiveCertainty}
                    onChange={(e) => setMetacognitiveCertainty(parseInt(e.target.value))}
                    className="confidence-slider clean"
                  />
                  <div className="confidence-value">{metacognitiveCertainty}%</div>
                </div>
              </div>

              {/* Time Pressure Slider Section */}
              <div className="confidence-section">
                <p className="section-instruction" style={{ marginBottom: '0.3rem', fontSize: '0.85rem' }}>How much time pressure did you feel making this decision?</p>
                <div className="confidence-slider-container">
                  <div className="slider-labels">
                    <span>No pressure</span>
                    <span>Extreme pressure</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={perceivedTimePressure}
                    onChange={(e) => setPerceivedTimePressure(parseInt(e.target.value))}
                    className="confidence-slider clean time-pressure-slider"
                  />
                  <div className="confidence-value">{perceivedTimePressure}%</div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary clean"
                  onClick={() => setCurrentStep(1)}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-primary clean"
                  title={confidence === 50 ? "Tip: Adjust confidence slider from default 50%" : ""}
                >
                  Submit
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default MicroSurvey;
